<div class="p-6 lg:p-8">

  <!-- Single toolbar: title + provider/region controls + account context + Manage -->
  <div class="mb-5 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm">

    <!-- Group 1: title -->
    <h1 class="text-base font-semibold text-slate-900">Cloud</h1>

    <!-- Group 2: provider + account selector — anchored next to the title -->
    <span class="hidden h-5 w-px bg-slate-200 sm:block" aria-hidden="true"></span>
    <app-provider-switcher [active]="provider" (providerChange)="onProviderChange($event)"></app-provider-switcher>

    <!-- Monitored-services indicator (AWS header). Azure shows its own footprint chip. -->
    <app-monitored-count
      *ngIf="provider === 'AWS' && (pageState === 'loading' || (pageState === 'connected' && !hasNoAccounts))"
      [loading]="pageState === 'loading' || !monitoringLoaded"
      [count]="monitoredCount"
    ></app-monitored-count>

    <!-- Account selector (connected, multiple AWS accounts) -->
    <div *ngIf="provider === 'AWS' && pageState === 'connected' && showAccountSwitcher" class="flex items-center gap-1.5">
      <lucide-icon name="cloud" [size]="13" class="text-slate-400" [strokeWidth]="1.75"></lucide-icon>
      <label for="cloud-account" class="sr-only">AWS account</label>
      <select
        id="cloud-account"
        [ngModel]="account"
        (ngModelChange)="onAccountChange($event)"
        class="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-800 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200"
      >
        <option *ngFor="let a of accounts" [value]="a.accountId">{{ a.label || a.accountId }}</option>
      </select>
    </div>

    <!-- Group 3: account context (role behind disclosure) + region selector + Manage — right-anchored -->
    <ng-container *ngIf="provider === 'AWS' && pageState === 'connected' && summary">
      <div class="ml-auto flex items-center gap-2">
        <app-cloud-summary-bar [summary]="summary"></app-cloud-summary-bar>

        <div *ngIf="showRegionSwitcher" class="flex items-center gap-1">
          <label for="cloud-region" class="sr-only">Region</label>
          <select
            id="cloud-region"
            [ngModel]="scope"
            (ngModelChange)="onScopeChange($event)"
            class="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-800 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200"
          >
            <option [value]="ALL_REGIONS">All enabled regions</option>
            <option *ngFor="let r of summary?.enabledRegions" [value]="r">{{ r }}</option>
          </select>
          <span class="group relative inline-flex">
            <lucide-icon
              name="info" [size]="14" class="cursor-help text-slate-400 transition-colors hover:text-slate-600" [strokeWidth]="1.75"
              tabindex="0" role="img"
              aria-label="Regional counts reflect the selected scope. Global services like CloudFront are always shown."
            ></lucide-icon>
            <span role="tooltip" class="pointer-events-none absolute right-0 top-full z-20 mt-2 w-56 rounded-lg bg-slate-800 px-2.5 py-2 text-[11px] leading-snug text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100">
              Regional counts reflect the selected scope. Global services (CloudFront) are always shown.
            </span>
          </span>
        </div>

        <a
          routerLink="/settings/integrations"
          class="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
        >
          Manage
        </a>
      </div>
    </ng-container>

    <!-- ── Azure header controls — the parallel of the AWS controls above:
         monitored count · subscription selector · location selector · Manage ── -->
    <app-monitored-count
      *ngIf="provider === 'AZURE' && (azureState === 'loading' || azureState === 'connected')"
      [loading]="azureState === 'loading' || !azureMonitoringLoaded"
      [count]="azureMonitoredCount"
    ></app-monitored-count>

    <!-- Subscription selector (multiple Azure subscriptions) -->
    <div *ngIf="provider === 'AZURE' && azureState === 'connected' && showAzureSubscriptionSwitcher" class="flex items-center gap-1.5">
      <lucide-icon name="cloud" [size]="13" class="text-slate-400" [strokeWidth]="1.75"></lucide-icon>
      <label for="cloud-subscription" class="sr-only">Azure subscription</label>
      <select
        id="cloud-subscription"
        [ngModel]="azureSubscription"
        (ngModelChange)="onAzureSubscriptionChange($event)"
        class="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-800 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200"
      >
        <option *ngFor="let s of azureSubscriptions" [value]="s.subscriptionId">{{ s.label || s.subscriptionId }}</option>
      </select>
    </div>

    <ng-container *ngIf="provider === 'AZURE' && azureState === 'connected'">
      <div class="ml-auto flex items-center gap-2">
        <!-- Single-subscription context chip (parallel to the AWS summary bar) -->
        <span *ngIf="!showAzureSubscriptionSwitcher" class="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600">
          <lucide-icon name="cloud" [size]="12" class="text-slate-400" [strokeWidth]="1.75"></lucide-icon>
          <span class="font-mono text-slate-800">{{ azureSubscriptionLabel }}</span>
        </span>

        <div *ngIf="showAzureLocationSwitcher" class="flex items-center gap-1">
          <label for="cloud-location" class="sr-only">Location</label>
          <select
            id="cloud-location"
            [ngModel]="azureLocation"
            (ngModelChange)="onAzureLocationChange($event)"
            class="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-800 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200"
          >
            <option [value]="ALL_REGIONS">All enabled locations</option>
            <option *ngFor="let l of azureEnabledLocations" [value]="l">{{ l }}</option>
          </select>
          <span class="group relative inline-flex">
            <lucide-icon
              name="info" [size]="14" class="cursor-help text-slate-400 transition-colors hover:text-slate-600" [strokeWidth]="1.75"
              tabindex="0" role="img"
              aria-label="Location counts reflect the selected scope. Global services like Front Door are always shown."
            ></lucide-icon>
            <span role="tooltip" class="pointer-events-none absolute right-0 top-full z-20 mt-2 w-56 rounded-lg bg-slate-800 px-2.5 py-2 text-[11px] leading-snug text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100">
              Location counts reflect the selected scope. Global services (Front Door) are always shown.
            </span>
          </span>
        </div>

        <a
          routerLink="/settings/integrations"
          class="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
        >
          Manage
        </a>
      </div>
    </ng-container>

    <!-- ── GCP header controls — the parallel of the AWS/Azure controls above:
         monitored count · project selector · region selector · Manage ── -->
    <app-monitored-count
      *ngIf="provider === 'GCP' && (gcpState === 'loading' || gcpState === 'connected')"
      [loading]="gcpState === 'loading' || !gcpMonitoringLoaded"
      [count]="gcpMonitoredCount"
    ></app-monitored-count>

    <!-- Project selector (multiple GCP projects) -->
    <div *ngIf="provider === 'GCP' && gcpState === 'connected' && showGcpProjectSwitcher" class="flex items-center gap-1.5">
      <lucide-icon name="cloud" [size]="13" class="text-slate-400" [strokeWidth]="1.75"></lucide-icon>
      <label for="cloud-project" class="sr-only">GCP project</label>
      <select
        id="cloud-project"
        [ngModel]="gcpProject"
        (ngModelChange)="onGcpProjectChange($event)"
        class="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-800 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200"
      >
        <option *ngFor="let p of gcpProjects" [value]="p.projectId">{{ p.label || p.projectId }}</option>
      </select>
    </div>

    <ng-container *ngIf="provider === 'GCP' && gcpState === 'connected'">
      <div class="ml-auto flex items-center gap-2">
        <!-- Single-project context chip (parallel to the AWS summary bar) -->
        <span *ngIf="!showGcpProjectSwitcher" class="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600">
          <lucide-icon name="cloud" [size]="12" class="text-slate-400" [strokeWidth]="1.75"></lucide-icon>
          <span class="font-mono text-slate-800">{{ gcpProjectLabel }}</span>
        </span>

        <div *ngIf="showGcpRegionSwitcher" class="flex items-center gap-1">
          <label for="cloud-gcp-region" class="sr-only">Region</label>
          <select
            id="cloud-gcp-region"
            [ngModel]="gcpRegion"
            (ngModelChange)="onGcpRegionChange($event)"
            class="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-800 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200"
          >
            <option [value]="ALL_REGIONS">All enabled regions</option>
            <option *ngFor="let r of gcpEnabledRegions" [value]="r">{{ r }}</option>
          </select>
          <span class="group relative inline-flex">
            <lucide-icon
              name="info" [size]="14" class="cursor-help text-slate-400 transition-colors hover:text-slate-600" [strokeWidth]="1.75"
              tabindex="0" role="img"
              aria-label="Region counts reflect the selected scope. Global services like Cloud CDN are always shown."
            ></lucide-icon>
            <span role="tooltip" class="pointer-events-none absolute right-0 top-full z-20 mt-2 w-56 rounded-lg bg-slate-800 px-2.5 py-2 text-[11px] leading-snug text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100">
              Region counts reflect the selected scope. Global services (Cloud CDN) are always shown.
            </span>
          </span>
        </div>

        <a
          routerLink="/settings/integrations"
          class="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
        >
          Manage
        </a>
      </div>
    </ng-container>

  </div>

  <!-- ── AWS provider body ─────────────────────────────────────────────── -->
  <!-- ── Loading skeleton ──────────────────────────────────────────────── -->
  <ng-container *ngIf="provider === 'AWS' && pageState === 'loading'">
    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" aria-hidden="true">
      <div *ngFor="let _ of skeletonItems" class="h-32 animate-pulse rounded-xl bg-slate-200"></div>
    </div>
    <span class="sr-only" role="status">Loading cloud data…</span>
  </ng-container>

  <!-- ── Not connected ─────────────────────────────────────────────────── -->
  <ng-container *ngIf="provider === 'AWS' && pageState === 'not-connected'">
    <div class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div class="flex flex-col items-center gap-5 px-8 py-16 text-center">

        <div class="flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100">
          <lucide-icon name="cloud" [size]="28" class="text-slate-400" [strokeWidth]="1.5"></lucide-icon>
        </div>

        <div>
          <h3 class="text-sm font-semibold text-slate-900">Connect your AWS account</h3>
          <p class="mt-1.5 max-w-sm text-sm text-slate-500">
            Use a cross-account IAM role to let KernalHQ monitor your AWS infrastructure in real time — no access keys stored.
          </p>
        </div>

        <!-- Service preview chips (alphabetical) -->
        <div class="flex flex-wrap justify-center gap-2">
          <span class="flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600">
            <lucide-icon name="globe" [size]="11" [strokeWidth]="2"></lucide-icon> API Gateway
          </span>
          <span class="flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600">
            <lucide-icon name="workflow" [size]="11" [strokeWidth]="2"></lucide-icon> AppSync
          </span>
          <span class="flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600">
            <lucide-icon name="git-branch" [size]="11" [strokeWidth]="2"></lucide-icon> CodePipeline
          </span>
          <span class="flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600">
            <lucide-icon name="users" [size]="11" [strokeWidth]="2"></lucide-icon> Cognito
          </span>
          <span class="flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600">
            <lucide-icon name="table" [size]="11" [strokeWidth]="2"></lucide-icon> DynamoDB
          </span>
          <span class="flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600">
            <lucide-icon name="server" [size]="11" [strokeWidth]="2"></lucide-icon> EC2
          </span>
          <span class="flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600">
            <lucide-icon name="box" [size]="11" [strokeWidth]="2"></lucide-icon> ECS
          </span>
          <span class="flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600">
            <lucide-icon name="database" [size]="11" [strokeWidth]="2"></lucide-icon> Glue
          </span>
          <span class="flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600">
            <lucide-icon name="zap" [size]="11" [strokeWidth]="2"></lucide-icon> Lambda
          </span>
          <span class="flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600">
            <lucide-icon name="database" [size]="11" [strokeWidth]="2"></lucide-icon> RDS
          </span>
          <span class="flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600">
            <lucide-icon name="git-graph" [size]="11" [strokeWidth]="2"></lucide-icon> Step Functions
          </span>
        </div>

        <a
          routerLink="/settings/integrations"
          class="mt-1 flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2"
        >
          <lucide-icon name="settings" [size]="14" [strokeWidth]="2"></lucide-icon>
          Connect AWS Account
        </a>

      </div>
    </div>
  </ng-container>

  <!-- ── Error ─────────────────────────────────────────────────────────── -->
  <ng-container *ngIf="provider === 'AWS' && pageState === 'error'">
    <div class="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3" role="alert">
      <lucide-icon name="triangle-alert" [size]="15" class="mt-0.5 flex-shrink-0 text-red-500" [strokeWidth]="2"></lucide-icon>
      <div>
        <p class="text-sm text-red-700">Failed to load cloud connection status.</p>
        <a routerLink="/settings/integrations" class="mt-0.5 inline-block text-xs font-medium text-red-600 underline hover:text-red-800">
          Check connection settings →
        </a>
      </div>
    </div>
  </ng-container>

  <!-- ── Connected but no AWS accounts available ───────────────────────── -->
  <ng-container *ngIf="provider === 'AWS' && hasNoAccounts">
    <div class="flex flex-col items-center gap-4 rounded-xl border border-slate-200 bg-white px-8 py-14 text-center shadow-sm">
      <div class="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100">
        <lucide-icon name="cloud" [size]="26" class="text-slate-400" [strokeWidth]="1.5"></lucide-icon>
      </div>
      <div>
        <h3 class="text-sm font-semibold text-slate-900">No AWS accounts available</h3>
        <p class="mt-1.5 max-w-sm text-sm text-slate-500">Your AWS integration is connected but no accounts were discovered yet. Add an AWS account to start monitoring.</p>
      </div>
      <a routerLink="/settings/integrations" class="rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2">
        Add AWS account
      </a>
    </div>
  </ng-container>

  <!-- ── Connected ─────────────────────────────────────────────────────── -->
  <ng-container *ngIf="provider === 'AWS' && pageState === 'connected' && summary && !hasNoAccounts">

    <!-- Service cards — full-width category grid; each card links to its detail page -->
    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <app-service-card
        *ngFor="let card of serviceCards"
        [card]="card"
        [loading]="isCardLoading(card.id)"
        [refreshing]="isRefreshing(card.id)"
        [configSummary]="cloudService.configSummary(card.id)"
        [account]="account"
        [region]="scope"
        (settingsClick)="openSettings(card)"
        (refresh)="refreshCard(card)"
      ></app-service-card>
    </div>

  </ng-container>

  <!-- ── Azure provider body ───────────────────────────────────────────── -->
  <app-azure-overview
    *ngIf="provider === 'AZURE'"
    [connection]="azureConnection"
    [state]="azureState"
    [subscription]="azureSubscription"
    [location]="azureLocation"
    [monitoringLoaded]="azureMonitoringLoaded"
    [monitoredCount]="azureMonitoredCount"
  ></app-azure-overview>

  <!-- ── GCP provider body ─────────────────────────────────────────────── -->
  <app-gcp-overview
    *ngIf="provider === 'GCP'"
    [connection]="gcpConnection"
    [state]="gcpState"
    [project]="gcpProject"
    [region]="gcpRegion"
    [monitoringLoaded]="gcpMonitoringLoaded"
    [monitoredCount]="gcpMonitoredCount"
  ></app-gcp-overview>

</div>

<!-- Service settings drawer — rendered outside the page scroll area so it can use fixed positioning -->
<app-service-settings-drawer
  [service]="drawerService"
  [open]="drawerService !== null"
  (closed)="closeSettings()"
></app-service-settings-drawer>

